import { yupResolver } from '@hookform/resolvers/yup';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import { isNaN } from 'lodash';
import { useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { PricingCategoryEnumType } from '@/types/enumTypes';
import type { CreditsShoppingCartPricing } from '@/types/PricingCategory';
import type { PricingCategory } from '@/utils/utils';
import {
  ConvertAmountFormat,
  informationPricingCategoryDetails,
} from '@/utils/utils';

import CustomTextMask from './CustomTextMask';
import InformationReturnsPricingDetails from './InformationReturnsPricingDetails';

interface Props {
  formName: string;
  colorCode: string;
}

type PricingSubmitType = {
  InfoSkuCount: number;
  InfoStateSkuCount: number;
  InfoPostalSkuCount: number;
  InfoOnlineSkuCount: number;
  EmailAddress: string;
  Name: string;
};

const PricingDetailsComp = (props: Props) => {
  const [creditsPricingCategory, setCreditsPricingCategory] = useState<
    CreditsShoppingCartPricing[]
  >([]);
  const [infoPriceDetailsOpen, setInfoPriceDetailsOpen] = useState(false);
  const [pricingCategories] = useState<PricingCategory[]>(
    informationPricingCategoryDetails
  );

  const validationSchema = Yup.object().shape(
    {
      Name: Yup.string()
        .required('')
        .matches(/([A-Za-z] ?)*[A-Za-z]/, 'Please enter a valid Name'),
      EmailAddress: Yup.string().email('Email Address is invalid').required(''),
    },
    []
  );

  const { control, getValues, setValue } = useForm<PricingSubmitType>({
    mode: 'onChange',
    defaultValues: {
      InfoSkuCount: 1,
      InfoStateSkuCount: 1,
      InfoPostalSkuCount: 1,
      InfoOnlineSkuCount: 1,
    },
    resolver: yupResolver(validationSchema) as unknown as Resolver<
      PricingSubmitType,
      any
    >,
  });

  const onChangeCredits = () => {
    const credits =
      Number(getValues(`InfoStateSkuCount`) ?? 0) +
      Number(getValues(`InfoPostalSkuCount`) ?? 0) +
      Number(getValues(`InfoSkuCount`) ?? 0) +
      Number(getValues(`InfoOnlineSkuCount`) ?? 0);
    if (credits > 0 && pricingCategories.length > 0) {
      let totalRangeCount = 0;
      let skipCategory = '';
      const pricingCategory: CreditsShoppingCartPricing[] = [];
      // eslint-disable-next-line no-plusplus
      for (let x: number = 0; x <= pricingCategories.length; x++) {
        let creditsCount = 0;
        if (
          pricingCategories[x]?.PricingCategoryType?.toString() ===
          PricingCategoryEnumType.StateFiling.toString()
        ) {
          creditsCount = Number(getValues(`InfoStateSkuCount`) ?? 0);
        } else if (
          pricingCategories[x]?.PricingCategoryType?.toString() ===
          PricingCategoryEnumType.PostalMailing.toString()
        ) {
          creditsCount = Number(getValues(`InfoPostalSkuCount`) ?? 0);
        } else if (
          pricingCategories[x]?.PricingCategoryType?.toString() ===
          PricingCategoryEnumType.FederalFiling.toString()
        ) {
          creditsCount = Number(getValues(`InfoSkuCount`) ?? 0);
        } else if (
          pricingCategories[x]?.PricingCategoryType?.toString() ===
          PricingCategoryEnumType.OnlineFiling.toString()
        ) {
          creditsCount = Number(getValues(`InfoOnlineSkuCount`) ?? 0);
        }
        if (
          creditsCount > 0 &&
          skipCategory !== pricingCategories[x]?.PricingCategoryType.toString()
        ) {
          if (
            pricingCategories[x]?.PricingCategoryType.toString() ===
              PricingCategoryEnumType.StateFiling.toString() ||
            pricingCategories[x]?.PricingCategoryType.toString() ===
              PricingCategoryEnumType.PostalMailing.toString() ||
            pricingCategories[x]?.PricingCategoryType.toString() ===
              PricingCategoryEnumType.OnlineFiling.toString()
          ) {
            totalRangeCount = 0;
          }
          const pricing = {} as CreditsShoppingCartPricing;
          // @ts-ignore
          pricing.PricingCategoryType =
            pricingCategories[x]?.PricingCategoryType;
          // @ts-ignore
          pricing.PricingcategoryId = pricingCategories[x]?.PricingCategoryId;
          // @ts-ignore
          pricing.PerFormRate = pricingCategories[x]?.Amount;
          // @ts-ignore
          pricing.FromCount = pricingCategories[x]?.FromCount;

          if (
            // @ts-ignore
            creditsCount <= (pricingCategories[x]?.ToCount ?? 0) ||
            // @ts-ignore
            (pricingCategories[x]?.ToCount ?? 0) === -1
          ) {
            pricing.NoofForm = creditsCount - totalRangeCount;
            // @ts-ignore
            pricing.Amount =
              pricing.NoofForm * (pricingCategories[x]?.Amount ?? 0);
            pricing.ToCount = creditsCount;
            pricingCategory.push(pricing);
            skipCategory = pricing.PricingCategoryType.toString();
          } else {
            // @ts-ignore
            pricing.NoofForm =
              (pricingCategories[x]?.ToCount ?? 0) - totalRangeCount;
            // @ts-ignore
            pricing.Amount =
              pricing.NoofForm * (pricingCategories[x]?.Amount ?? 0);
            // @ts-ignore
            totalRangeCount = pricingCategories[x]?.ToCount ?? 0;
            pricing.ToCount = totalRangeCount;
            pricingCategory.push(pricing);
          }
        }
      }
      setCreditsPricingCategory(pricingCategory);
    } else {
      setCreditsPricingCategory([]);
    }
  };

  const changeCreditValue = (isIncrement: boolean) => {
    let count = Number(getValues('InfoSkuCount') ?? 0);
    count = isNaN(count) ? 0 : count;
    if (isIncrement) {
      count += 1;
      setValue('InfoSkuCount', count);
    } else if (count !== 0) {
      count -= 1;
      setValue('InfoSkuCount', count);
    }
    onChangeCredits();
  };

  const changeStateCreditValue = (isIncrement: boolean) => {
    let count = Number(getValues('InfoStateSkuCount') ?? 0);
    count = isNaN(count) ? 0 : count;
    if (isIncrement) {
      count += 1;
      setValue('InfoStateSkuCount', count);
    } else if (count !== 0) {
      count -= 1;
      setValue('InfoStateSkuCount', count);
    }
    onChangeCredits();
  };

  const changePostalCreditValue = (isIncrement: boolean) => {
    let count = Number(getValues('InfoPostalSkuCount') ?? 0);
    count = isNaN(count) ? 0 : count;
    if (isIncrement) {
      count += 1;
      setValue('InfoPostalSkuCount', count);
    } else if (count !== 0) {
      count -= 1;
      setValue('InfoPostalSkuCount', count);
    }
    onChangeCredits();
  };

  const changeOnlineCreditValue = (isIncrement: boolean) => {
    let count = Number(getValues('InfoOnlineSkuCount') ?? 0);
    count = isNaN(count) ? 0 : count;
    if (isIncrement) {
      count += 1;
      setValue('InfoOnlineSkuCount', count);
    } else if (count !== 0) {
      count -= 1;
      setValue('InfoOnlineSkuCount', count);
    }
    onChangeCredits();
  };
  // const StateHide = ()=>{
  //   constructor() {
  //     super();
  //     this.state = {
  //       formName: ["1099-C","1099-S", "1099-SA"]
  //     };
  //   }
  // };
  // render() {
  //   const { showHideDemo1 } = this.props;
  // }
  return (
    <Box>
      <Grid container spacing={2} m={0} className="MuiGrid-root-width">
        <Grid size={12} sx={{ pt: '0 !important' }}>
          <Box component="div">
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file{' '}
              <span style={{ color: props.colorCode }}>{props.formName}</span>{' '}
              Pricing Calculator
            </Typography>
            <Grid container spacing={2} className="MuiGrid-root-width">
              <Grid size={12} textAlign="center">
                <Box
                  sx={{
                    p: 0,
                    py: { xs: 0, sm: 0, md: 0 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderStyle: 'solid',
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                    maxWidth: 836,
                    mx: 'auto',
                  }}
                >
                  <TableContainer>
                    <Table
                      sx={{
                        '& tr td:first-of-type': {
                          py: { xs: 0.5, sm: 1 },

                          borderBottom: {
                            xs: '1px solid #a6a6a6',
                            sm: '1px solid #a6a6a6',
                          },
                          textAlign: { xs: 'center', sm: 'left' },
                        },
                        '& tr td:last-child': {
                          py: { xs: 0.25, sm: 0.5 },
                          borderBottom: {
                            xs: '1px solid #a6a6a6',
                            sm: '1px solid #a6a6a6',
                          },
                          textAlign: { xs: 'center', sm: 'center' },
                        },
                        '& tr:last-child td': { border: 0 },
                      }}
                      className="table-list"
                    >
                      <TableHead
                        sx={{ borderBottom: 2, borderColor: '#a6a6a6' }}
                      >
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                              fontWeight: 600,
                              borderRadius: '8px 0px 0px 0px',
                              backgroundColor: '#f5f5f5',
                              textAlign: { xs: 'center', sm: 'left' },
                            }}
                          >
                            No. of Forms
                          </TableCell>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                              width: { xs: 150, sm: 180 },
                              textAlign: { xs: 'center', sm: 'center' },
                              fontWeight: 600,
                              borderRadius: '0px 8px 0px 0px',
                              backgroundColor: '#f5f5f5',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            Price Per Form
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            1 to 25
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#4269e2',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $2.49
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            26 to 50
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $1.99
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            51 to 100
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $1.59
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            101 to 250
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $1.29
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            251 to 500
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $1.09
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            501 to 1000
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $0.79
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            1001 and above
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: '2px solid #a6a6a6 !important',
                            }}
                          >
                            $0.59
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              m={2}
              mt={0}
              pb={2}
              mb={6}
              className="MuiGrid-root-width"
              sx={{ borderBottom: ' 1px solid #ccc' }}
            >
              <Grid size={12} textAlign="center">
                <Box
                  sx={{
                    width: '100%',
                    fontWeight: 400,
                    fontSize: 15,
                    maxWidth: 836,
                    mx: 'auto',
                    textAlign: 'left',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, mb: 0.5, fontSize: 18 }}
                  >
                    Estimate Your Price
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    fontWeight: 400,
                    fontSize: 15,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    maxWidth: 836,
                    mx: 'auto',
                  }}
                >
                  <TableContainer>
                    <Table
                      sx={{
                        '& td': {
                          display: { xs: 'block', sm: 'revert' },
                        },
                        '& tr td:first-of-type': {
                          py: { xs: 0.5, sm: 1 },
                        },
                        '& tr td:last-child': {
                          py: { xs: 0.25, sm: 0.5 },
                          textAlign: { xs: 'right', sm: 'center' },
                        },
                        '& tr td': { border: 0 },
                      }}
                    >
                      <TableBody>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 16,
                              textAlign: { xs: 'left', sm: 'right' },
                              fontWeight: 500,
                              color: 'rgba(0, 0, 0, 0.6)',
                            }}
                          >
                            Enter the number of to calculate your estimated cost
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#4269e2',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                              width: { sm: 180 },
                              px: 0,
                            }}
                          >
                            <Box>
                              <Controller
                                name="InfoSkuCount"
                                control={control}
                                defaultValue={1}
                                render={({
                                  field: { onChange, value },
                                  fieldState: { error },
                                }) => (
                                  <TextField
                                    id="InfoSkuCount"
                                    label=""
                                    variant="outlined"
                                    color="secondary"
                                    value={value?.toString()}
                                    size="small"
                                    sx={{
                                      '& .MuiInputBase-root': {
                                        '& input': {
                                          textAlign: 'center',
                                        },
                                      },
                                    }}
                                    onChange={(event) => {
                                      onChange(event);
                                      onChangeCredits();
                                    }}
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                    InputProps={{
                                      inputComponent: CustomTextMask as any,
                                      inputProps: {
                                        mask: 'num',
                                        blocks: {
                                          num: {
                                            min: 1,
                                            max: 9999999,
                                            mask: Number,
                                            scale: 0,
                                          },
                                          inputMode: 'numeric',
                                          maxLength: 7,
                                        },
                                      },
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <IconButton
                                            aria-label=""
                                            disabled={value <= 1}
                                            onClick={() =>
                                              changeCreditValue(false)
                                            }
                                            edge="end"
                                          >
                                            <RemoveOutlinedIcon />
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            aria-label=""
                                            disabled={value >= 9999999}
                                            onClick={() =>
                                              changeCreditValue(true)
                                            }
                                            edge="end"
                                          >
                                            <AddOutlinedIcon />
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <TableContainer
                  sx={{
                    width: '100%',
                    fontWeight: 400,
                    fontSize: 15,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    maxWidth: 836,
                    mx: 'auto',
                    mt: 1,
                  }}
                >
                  <Table
                    sx={{
                      '& td': {},
                      '& tr td:first-of-type': {
                        py: { xs: 0.5, sm: 1 },
                      },
                      '& tr td:last-child': {
                        py: { xs: 0.25, sm: 0.5 },
                        textAlign: { xs: 'right', sm: 'center' },
                      },
                      '& tr td': { border: 0 },
                    }}
                  >
                    <TableBody>
                      <TableRow>
                        <TableCell
                          scope="row"
                          sx={{
                            fontSize: 18,
                            textAlign: { xs: 'left', sm: 'right' },
                            fontWeight: 600,
                          }}
                        >
                          Total Price:
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: '#4269e2',
                            borderLeft: 2,
                            borderColor: '#a6a6a6',
                            width: { sm: 180 },
                            position: 'relative',
                            px: 3,
                          }}
                        >
                          {ConvertAmountFormat(
                            creditsPricingCategory
                              .filter(
                                (x) =>
                                  x.PricingCategoryType?.toString() ===
                                  PricingCategoryEnumType.FederalFiling.toString()
                              )
                              .reduce(
                                (previousValue, currentValue) =>
                                  previousValue + currentValue.Amount,
                                0
                              )
                          )}
                          {creditsPricingCategory
                            .filter(
                              (x) =>
                                x.PricingCategoryType?.toString() ===
                                PricingCategoryEnumType.FederalFiling.toString()
                            )
                            .reduce(
                              (previousValue, currentValue) =>
                                previousValue + currentValue.NoofForm,
                              0
                            ) > 25 && (
                            <Tooltip
                              title={`${
                                infoPriceDetailsOpen === false
                                  ? 'View the breakdown of'
                                  : 'Hide'
                              }`}
                              arrow
                            >
                              <IconButton
                                onClick={() =>
                                  setInfoPriceDetailsOpen(
                                    infoPriceDetailsOpen === false
                                  )
                                }
                                sx={{
                                  background: 'rgba(0, 0, 0, 0.85)',
                                  position: 'absolute',
                                  right: 0,
                                  top: 8,
                                  p: 0,
                                  '&:hover, &:active, &:focus': {
                                    background: 'rgba(0, 0, 0, 1)',
                                  },
                                }}
                              >
                                {infoPriceDetailsOpen === false ? (
                                  <KeyboardArrowDownIcon
                                    sx={{ color: '#fff' }}
                                  />
                                ) : (
                                  <KeyboardArrowUpIcon sx={{ color: '#fff' }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                {infoPriceDetailsOpen === true && (
                  <Box
                    sx={{
                      background: '#fff',
                      borderRadius: 3,
                      boxShadow: '0 0 20px rgba(0,0,0,.12)',
                      p: 1.5,
                      maxWidth: 836,
                      mx: 'auto',
                    }}
                  >
                    <InformationReturnsPricingDetails
                      creditsPricingCategory={creditsPricingCategory.filter(
                        (x) =>
                          x.PricingCategoryType?.toString() ===
                          PricingCategoryEnumType.FederalFiling.toString()
                      )}
                    />
                    {creditsPricingCategory
                      .filter(
                        (x) =>
                          x.PricingCategoryType?.toString() ===
                          PricingCategoryEnumType.FederalFiling.toString()
                      )
                      .reduce(
                        (previousValue, currentValue) =>
                          previousValue + currentValue.NoofForm,
                        0
                      ) > 0 && (
                      <Stack
                        direction="row"
                        justifyContent="space-around"
                        alignItems="center"
                        spacing={2}
                        sx={{ padding: 2 }}
                      >
                        <Box
                          sx={{
                            width: '100%',
                            fontWeight: 400,
                            fontSize: 15,
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{ color: '#637381', fontWeight: 600 }}
                          >
                            Total (
                            {`${Number(
                              creditsPricingCategory
                                .filter(
                                  (x) =>
                                    x.PricingCategoryType?.toString() ===
                                    PricingCategoryEnumType.FederalFiling.toString()
                                )
                                .reduce(
                                  (previousValue, currentValue) =>
                                    previousValue + currentValue.NoofForm,
                                  0
                                )
                            )}`}
                            )
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: '100%',
                            fontWeight: 400,
                            fontSize: 15,
                            whiteSpace: 'nowrap',
                          }}
                        />
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#14181F',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            fontSize: 18,
                          }}
                        >
                          $
                          {creditsPricingCategory
                            .filter(
                              (x) =>
                                x.PricingCategoryType?.toString() ===
                                PricingCategoryEnumType.FederalFiling.toString()
                            )
                            .reduce(
                              (previousValue, currentValue) =>
                                previousValue + currentValue.Amount,
                              0
                            )
                            .toFixed(2) ?? '0.00'}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
            {/* <Box component={'hr'} className="border-single" sx={{ my: 2 }} /> */}
            {/* State Filing Start */}

            {props.formName !== 'Form 1099-A' &&
              props.formName !== 'Form 1099-C' &&
              props.formName !== 'Form 1099-S' &&
              props.formName !== 'Form 1099-SA' &&
              props.formName !== 'Form 1099-LTC' &&
              props.formName !== 'Form 1099-PATR' &&
              props.formName !== 'Form 1099-Q' &&
              props.formName !== 'Form 1099 Corrections' &&
              props.formName !== 'Form 1098-T' &&
              props.formName !== 'Form 1098-E' &&
              props.formName !== 'Form 1098' &&
              props.formName !== 'Form 1095-C' && (
                <>
                  <Grid container spacing={2} className="MuiGrid-root-width">
                    <Grid size={12} textAlign="center">
                      <Box
                        sx={{
                          p: 0,
                          py: { xs: 0, sm: 0, md: 0 },
                          textAlign: 'center',
                          borderWidth: 2,
                          borderStyle: 'solid',
                          borderColor: '#a6a6a6',
                          borderRadius: 2,
                          minHeight: '100%',
                          backgroundColor: '#fff',
                          maxWidth: 836,
                          mx: 'auto',
                        }}
                      >
                        <TableContainer>
                          <Table
                            sx={{
                              '& td': {
                                // display: { xs: 'block', sm: 'revert' },
                              },
                              '& tr td:first-of-type': {
                                py: { xs: 0.5, sm: 1 },

                                borderBottom: {
                                  xs: '1px solid #a6a6a6',
                                  sm: '1px solid #a6a6a6',
                                },
                                textAlign: { xs: 'center', sm: 'left' },
                              },
                              '& tr td:last-child': {
                                py: { xs: 0.25, sm: 0.5 },
                                borderBottom: {
                                  xs: '1px solid #a6a6a6',
                                  sm: '1px solid #a6a6a6',
                                },
                                textAlign: { xs: 'center', sm: 'center' },
                              },
                              '& tr:last-child td': { border: 0 },
                            }}
                            className="table-list"
                          >
                            <TableHead
                              sx={{ borderBottom: 2, borderColor: '#a6a6a6' }}
                            >
                              <TableRow>
                                <TableCell
                                  scope="row"
                                  sx={{
                                    fontSize: 18,
                                    fontWeight: 600,
                                    borderRadius: '8px 0px 0px 0px',
                                    backgroundColor: '#f5f5f5',
                                    textAlign: { xs: 'center', sm: 'left' },
                                  }}
                                >
                                  W-2/1099 State Filing
                                </TableCell>
                                <TableCell
                                  scope="row"
                                  sx={{
                                    fontSize: 18,
                                    width: { xs: 150, sm: 180 },
                                    textAlign: { xs: 'center', sm: 'center' },
                                    fontWeight: 600,
                                    borderRadius: '0px 8px 0px 0px',
                                    backgroundColor: '#f5f5f5',
                                    borderLeft: 2,
                                    borderColor: '#a6a6a6',
                                  }}
                                >
                                  Price Per Form
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell
                                  scope="row"
                                  sx={{
                                    fontSize: 18,
                                  }}
                                >
                                  Per Form
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontSize: 24,
                                    fontWeight: 700,
                                    borderLeft: '2px solid #a6a6a6 !important',
                                  }}
                                >
                                  $0.99
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                    m={2}
                    mt={0}
                    pb={2}
                    mb={6}
                    className="MuiGrid-root-width"
                    sx={{ borderBottom: ' 1px solid #ccc' }}
                  >
                    <Grid size={12} textAlign="center">
                      <Box
                        sx={{
                          width: '100%',
                          fontWeight: 400,
                          fontSize: 15,
                          maxWidth: 836,
                          mx: 'auto',
                          textAlign: 'left',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, mb: 0.5, fontSize: 18 }}
                        >
                          Estimate Your Price
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          fontWeight: 400,
                          fontSize: 15,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          maxWidth: 836,
                          mx: 'auto',
                        }}
                      >
                        <TableContainer>
                          <Table
                            sx={{
                              '& td': {
                                display: { xs: 'block', sm: 'revert' },
                              },
                              '& tr td:first-of-type': {
                                py: { xs: 0.5, sm: 1 },
                              },
                              '& tr td:last-child': {
                                py: { xs: 0.25, sm: 0.5 },
                                textAlign: { xs: 'right', sm: 'center' },
                              },
                              '& tr td': { border: 0 },
                            }}
                          >
                            <TableBody>
                              <TableRow>
                                <TableCell
                                  scope="row"
                                  sx={{
                                    fontSize: 16,
                                    textAlign: { xs: 'left', sm: 'right' },
                                    fontWeight: 500,
                                    color: 'rgba(0, 0, 0, 0.6)',
                                  }}
                                >
                                  Enter the number of to calculate your
                                  estimated cost
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontSize: 24,
                                    fontWeight: 700,
                                    color: '#4269e2',
                                    borderLeft: 2,
                                    borderColor: '#a6a6a6',
                                    width: { sm: 180 },
                                    px: 0,
                                  }}
                                >
                                  <Box>
                                    <Controller
                                      name="InfoStateSkuCount"
                                      control={control}
                                      defaultValue={1}
                                      render={({
                                        field: { onChange, value },
                                        fieldState: { error },
                                      }) => (
                                        <TextField
                                          id="InfoStateSkuCount"
                                          label=""
                                          variant="outlined"
                                          color="secondary"
                                          value={value?.toString()}
                                          size="small"
                                          sx={{
                                            '& .MuiInputBase-root': {
                                              '& input': {
                                                textAlign: 'center',
                                              },
                                            },
                                          }}
                                          onChange={(event) => {
                                            onChange(event);
                                            onChangeCredits();
                                          }}
                                          error={!!error}
                                          helperText={
                                            error ? error.message : null
                                          }
                                          InputProps={{
                                            inputComponent:
                                              CustomTextMask as any,
                                            inputProps: {
                                              mask: 'num',
                                              blocks: {
                                                num: {
                                                  min: 1,
                                                  max: 9999999,
                                                  mask: Number,
                                                  scale: 0,
                                                },
                                                inputMode: 'numeric',
                                                maxLength: 7,
                                              },
                                            },
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                <IconButton
                                                  aria-label=""
                                                  disabled={value <= 1}
                                                  onClick={() =>
                                                    changeStateCreditValue(
                                                      false
                                                    )
                                                  }
                                                  edge="end"
                                                >
                                                  <RemoveOutlinedIcon />
                                                </IconButton>
                                              </InputAdornment>
                                            ),
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                <IconButton
                                                  aria-label=""
                                                  disabled={value >= 9999999}
                                                  onClick={() =>
                                                    changeStateCreditValue(true)
                                                  }
                                                  edge="end"
                                                >
                                                  <AddOutlinedIcon />
                                                </IconButton>
                                              </InputAdornment>
                                            ),
                                          }}
                                        />
                                      )}
                                    />
                                  </Box>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                      <TableContainer
                        sx={{
                          width: '100%',
                          fontWeight: 400,
                          fontSize: 15,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          maxWidth: 836,
                          mx: 'auto',
                          mt: 1,
                        }}
                      >
                        <Table
                          sx={{
                            '& td': {},
                            '& tr td:first-of-type': {
                              py: { xs: 0.5, sm: 1 },
                            },
                            '& tr td:last-child': {
                              py: { xs: 0.25, sm: 0.5 },
                              textAlign: { xs: 'right', sm: 'center' },
                            },
                            '& tr td': { border: 0 },
                          }}
                        >
                          <TableBody>
                            <TableRow>
                              <TableCell
                                scope="row"
                                sx={{
                                  fontSize: 18,
                                  textAlign: { xs: 'left', sm: 'right' },
                                  fontWeight: 600,
                                }}
                              >
                                Total Price:
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontSize: 20,
                                  fontWeight: 700,
                                  color: '#4269e2',
                                  borderLeft: 2,
                                  borderColor: '#a6a6a6',
                                  width: { sm: 180 },
                                  position: 'relative',
                                  px: 3,
                                }}
                              >
                                {ConvertAmountFormat(
                                  creditsPricingCategory
                                    .filter(
                                      (x) =>
                                        x.PricingCategoryType?.toString() ===
                                        PricingCategoryEnumType.StateFiling.toString()
                                    )
                                    .reduce(
                                      (previousValue, currentValue) =>
                                        previousValue + currentValue.Amount,
                                      0
                                    )
                                )}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </>
              )}

            {/* State Filing End */}
            {/* Postal Mailing Start */}
            <Grid container spacing={2} className="MuiGrid-root-width">
              <Grid size={12} textAlign="center">
                <Box
                  sx={{
                    p: 0,
                    py: { xs: 0, sm: 0, md: 0 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderStyle: 'solid',
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                    maxWidth: 836,
                    mx: 'auto',
                  }}
                >
                  <TableContainer>
                    <Table
                      sx={{
                        '& td': {
                          // display: { xs: 'block', sm: 'revert' },
                        },
                        '& tr td:first-of-type': {
                          py: { xs: 0.5, sm: 1 },

                          borderBottom: {
                            xs: '1px solid #a6a6a6',
                            sm: '1px solid #a6a6a6',
                          },
                          textAlign: { xs: 'center', sm: 'left' },
                        },
                        '& tr td:last-child': {
                          py: { xs: 0.25, sm: 0.5 },
                          borderBottom: {
                            xs: '1px solid #a6a6a6',
                            sm: '1px solid #a6a6a6',
                          },
                          textAlign: { xs: 'center', sm: 'center' },
                        },
                        '& tr:last-child td': { border: 0 },
                      }}
                      className="table-list"
                    >
                      <TableHead
                        sx={{ borderBottom: 2, borderColor: '#a6a6a6' }}
                      >
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                              fontWeight: 600,
                              borderRadius: '8px 0px 0px 0px',
                              backgroundColor: '#f5f5f5',
                              textAlign: { xs: 'center', sm: 'left' },
                            }}
                          >
                            W-2/1099/1095 Postal Mailing
                          </TableCell>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                              width: { xs: 150, sm: 180 },
                              textAlign: { xs: 'center', sm: 'center' },
                              fontWeight: 600,
                              borderRadius: '0px 8px 0px 0px',
                              backgroundColor: '#f5f5f5',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            Price Per Form
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            Per Form
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              borderLeft: '2px solid #a6a6a6 !important',
                            }}
                          >
                            $1.75
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              m={2}
              mt={0}
              pb={2}
              mb={6}
              className="MuiGrid-root-width"
              sx={{ borderBottom: ' 1px solid #ccc' }}
            >
              <Grid size={12} textAlign="center">
                <Box
                  sx={{
                    width: '100%',
                    fontWeight: 400,
                    fontSize: 15,
                    maxWidth: 836,
                    mx: 'auto',
                    textAlign: 'left',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, mb: 0.5, fontSize: 18 }}
                  >
                    Estimate Your Price
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    fontWeight: 400,
                    fontSize: 15,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    maxWidth: 836,
                    mx: 'auto',
                  }}
                >
                  <TableContainer>
                    <Table
                      sx={{
                        '& td': {
                          display: { xs: 'block', sm: 'revert' },
                        },
                        '& tr td:first-of-type': {
                          py: { xs: 0.5, sm: 1 },
                        },
                        '& tr td:last-child': {
                          py: { xs: 0.25, sm: 0.5 },
                          textAlign: { xs: 'right', sm: 'center' },
                        },
                        '& tr td': { border: 0 },
                      }}
                    >
                      <TableBody>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 16,
                              textAlign: { xs: 'left', sm: 'right' },
                              fontWeight: 500,
                              color: 'rgba(0, 0, 0, 0.6)',
                            }}
                          >
                            Enter the number of to calculate your estimated cost
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#4269e2',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                              width: { sm: 180 },
                              px: 0,
                            }}
                          >
                            <Box>
                              <Controller
                                name="InfoPostalSkuCount"
                                control={control}
                                defaultValue={1}
                                render={({
                                  field: { onChange, value },
                                  fieldState: { error },
                                }) => (
                                  <TextField
                                    id="InfoPostalSkuCount"
                                    label=""
                                    variant="outlined"
                                    color="secondary"
                                    value={value?.toString()}
                                    size="small"
                                    sx={{
                                      '& .MuiInputBase-root': {
                                        '& input': {
                                          textAlign: 'center',
                                        },
                                      },
                                    }}
                                    onChange={(event) => {
                                      onChange(event);
                                      onChangeCredits();
                                    }}
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                    InputProps={{
                                      inputComponent: CustomTextMask as any,
                                      inputProps: {
                                        mask: 'num',
                                        blocks: {
                                          num: {
                                            min: 1,
                                            max: 9999999,
                                            mask: Number,
                                            scale: 0,
                                          },
                                          inputMode: 'numeric',
                                          maxLength: 7,
                                        },
                                      },
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <IconButton
                                            aria-label=""
                                            disabled={value <= 1}
                                            onClick={() =>
                                              changePostalCreditValue(false)
                                            }
                                            edge="end"
                                          >
                                            <RemoveOutlinedIcon />
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            aria-label=""
                                            disabled={value >= 9999999}
                                            onClick={() =>
                                              changePostalCreditValue(true)
                                            }
                                            edge="end"
                                          >
                                            <AddOutlinedIcon />
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <TableContainer
                  sx={{
                    width: '100%',
                    fontWeight: 400,
                    fontSize: 15,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    maxWidth: 836,
                    mx: 'auto',
                    mt: 1,
                  }}
                >
                  <Table
                    sx={{
                      '& td': {},
                      '& tr td:first-of-type': {
                        py: { xs: 0.5, sm: 1 },
                      },
                      '& tr td:last-child': {
                        py: { xs: 0.25, sm: 0.5 },
                        textAlign: { xs: 'right', sm: 'center' },
                      },
                      '& tr td': { border: 0 },
                    }}
                  >
                    <TableBody>
                      <TableRow>
                        <TableCell
                          scope="row"
                          sx={{
                            fontSize: 18,
                            textAlign: { xs: 'left', sm: 'right' },
                            fontWeight: 600,
                          }}
                        >
                          Total Price:
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: '#4269e2',
                            borderLeft: 2,
                            borderColor: '#a6a6a6',
                            width: { sm: 180 },
                            position: 'relative',
                            px: 3,
                          }}
                        >
                          {ConvertAmountFormat(
                            creditsPricingCategory
                              .filter(
                                (x) =>
                                  x.PricingCategoryType?.toString() ===
                                  PricingCategoryEnumType.PostalMailing.toString()
                              )
                              .reduce(
                                (previousValue, currentValue) =>
                                  previousValue + currentValue.Amount,
                                0
                              )
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
            {/* Postal Filing End */}
            {/* Postal Mailing Start */}
            <Grid container spacing={2} className="MuiGrid-root-width">
              <Grid size={12} textAlign="center">
                <Box
                  sx={{
                    p: 0,
                    py: { xs: 0, sm: 0, md: 0 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderStyle: 'solid',
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                    maxWidth: 836,
                    mx: 'auto',
                  }}
                >
                  <TableContainer>
                    <Table
                      sx={{
                        '& tr td:first-of-type': {
                          py: { xs: 0.5, sm: 1 },

                          borderBottom: {
                            xs: '1px solid #a6a6a6',
                            sm: '1px solid #a6a6a6',
                          },
                          textAlign: { xs: 'center', sm: 'left' },
                        },
                        '& tr td:last-child': {
                          py: { xs: 0.25, sm: 0.5 },
                          borderBottom: {
                            xs: '1px solid #a6a6a6',
                            sm: '1px solid #a6a6a6',
                          },
                          textAlign: { xs: 'center', sm: 'center' },
                        },
                        '& tr:last-child td': { border: 0 },
                      }}
                      className="table-list"
                    >
                      <TableHead
                        sx={{ borderBottom: 2, borderColor: '#a6a6a6' }}
                      >
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                              fontWeight: 600,
                              borderRadius: '8px 0px 0px 0px',
                              backgroundColor: '#f5f5f5',
                              textAlign: { xs: 'center', sm: 'left' },
                            }}
                          >
                            W-2/1099/1095 Electronic Delivery
                          </TableCell>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                              width: { xs: 150, sm: 180 },
                              textAlign: { xs: 'center', sm: 'center' },
                              fontWeight: 600,
                              borderRadius: '0px 8px 0px 0px',
                              backgroundColor: '#f5f5f5',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            Price Per Form
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            Per Form
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              borderLeft: '2px solid #a6a6a6 !important',
                            }}
                          >
                            $0.50
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              m={2}
              mt={0}
              pb={2}
              mb={6}
              className="MuiGrid-root-width"
            >
              <Grid size={12} textAlign="center">
                <Box
                  sx={{
                    width: '100%',
                    fontWeight: 400,
                    fontSize: 15,
                    maxWidth: 836,
                    mx: 'auto',
                    textAlign: 'left',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, mb: 0.5, fontSize: 18 }}
                  >
                    Estimate Your Price
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    fontWeight: 400,
                    fontSize: 15,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    maxWidth: 836,
                    mx: 'auto',
                  }}
                >
                  <TableContainer>
                    <Table
                      sx={{
                        '& td': {
                          display: { xs: 'block', sm: 'revert' },
                        },
                        '& tr td:first-of-type': {
                          py: { xs: 0.5, sm: 1 },
                        },
                        '& tr td:last-child': {
                          py: { xs: 0.25, sm: 0.5 },
                          textAlign: { xs: 'right', sm: 'center' },
                        },
                        '& tr td': { border: 0 },
                      }}
                    >
                      <TableBody>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 16,
                              textAlign: { xs: 'left', sm: 'right' },
                              fontWeight: 500,
                              color: 'rgba(0, 0, 0, 0.6)',
                            }}
                          >
                            Enter the number of to calculate your estimated cost
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#4269e2',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                              width: { sm: 180 },
                              px: 0,
                            }}
                          >
                            <Box>
                              <Controller
                                name="InfoOnlineSkuCount"
                                control={control}
                                defaultValue={1}
                                render={({
                                  field: { onChange, value },
                                  fieldState: { error },
                                }) => (
                                  <TextField
                                    id="InfoOnlineSkuCount"
                                    label=""
                                    variant="outlined"
                                    color="secondary"
                                    value={value?.toString()}
                                    size="small"
                                    sx={{
                                      '& .MuiInputBase-root': {
                                        '& input': {
                                          textAlign: 'center',
                                        },
                                      },
                                    }}
                                    onChange={(event) => {
                                      onChange(event);
                                      onChangeCredits();
                                    }}
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                    InputProps={{
                                      inputComponent: CustomTextMask as any,
                                      inputProps: {
                                        mask: 'num',
                                        blocks: {
                                          num: {
                                            min: 1,
                                            max: 9999999,
                                            mask: Number,
                                            scale: 0,
                                          },
                                          inputMode: 'numeric',
                                          maxLength: 7,
                                        },
                                      },
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <IconButton
                                            aria-label=""
                                            disabled={value <= 1}
                                            onClick={() =>
                                              changeOnlineCreditValue(false)
                                            }
                                            edge="end"
                                          >
                                            <RemoveOutlinedIcon />
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            aria-label=""
                                            disabled={value >= 9999999}
                                            onClick={() =>
                                              changeOnlineCreditValue(true)
                                            }
                                            edge="end"
                                          >
                                            <AddOutlinedIcon />
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <TableContainer
                  sx={{
                    width: '100%',
                    fontWeight: 400,
                    fontSize: 15,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    maxWidth: 836,
                    mx: 'auto',
                    mt: 1,
                  }}
                >
                  <Table
                    sx={{
                      '& td': {},
                      '& tr td:first-of-type': {
                        py: { xs: 0.5, sm: 1 },
                      },
                      '& tr td:last-child': {
                        py: { xs: 0.25, sm: 0.5 },
                        textAlign: { xs: 'right', sm: 'center' },
                      },
                      '& tr td': { border: 0 },
                    }}
                  >
                    <TableBody>
                      <TableRow>
                        <TableCell
                          scope="row"
                          sx={{
                            fontSize: 18,
                            textAlign: { xs: 'left', sm: 'right' },
                            fontWeight: 600,
                          }}
                        >
                          Total Price:
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: '#4269e2',
                            borderLeft: 2,
                            borderColor: '#a6a6a6',
                            width: { sm: 180 },
                            position: 'relative',
                            px: 3,
                          }}
                        >
                          {ConvertAmountFormat(
                            creditsPricingCategory
                              .filter(
                                (x) =>
                                  x.PricingCategoryType?.toString() ===
                                  PricingCategoryEnumType.OnlineFiling.toString()
                              )
                              .reduce(
                                (previousValue, currentValue) =>
                                  previousValue + currentValue.Amount,
                                0
                              )
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
            {/* Electric Delivery End */}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PricingDetailsComp;
